import moment from "moment";
import {
	typeKey,
	typeColor,
	trainingTypeColors,
	trainingTypeValue,
	trainingTypePartial,
	trainingTypeShare,
} from "./trainingTypes";
export function appendTrainingTypesMatrixPDF(
	doc,
	data,
	rows,
	search,
	clean,
	metric = "minutes",
	scope = "completed",
) {
	const scheduled = scope === "scheduled";
	const summary = data.trainingAnalysis?.period[scope];
	const typeColors = trainingTypeColors(data.trainingAnalysis);
	const minutes = metric === "minutes";
	const unit = minutes ? " min" : " sed.";
	const types = summary?.types || [];
	const blocks = [];
	for (let i = 0; i < types.length; i += 5) blocks.push(types.slice(i, i + 5));
	if (!blocks.length) blocks.push([]);
	const margin = 12,
		width = doc.internal.pageSize.getWidth() - 24,
		nameWidth = 43,
		totalWidth = 26,
		coverageWidth = 23,
		rowHeight = 10,
		maxRows = 14;
	const number = (v) => (v == null ? "-" : v.toLocaleString("it-IT"));
	let pages = 0;
	const rect = (x, y, w, h, background) => {
		doc.setFillColor(background);
		doc.setDrawColor(220, 229, 239);
		doc.setLineWidth(0.15);
		doc.rect(x, y, w, h, "FD");
	};
	const text = (value, x, y, w, size = 7, bold = false) => {
		doc.setTextColor(23, 57, 95);
		doc.setFont("Matrix", bold ? "bold" : "normal");
		doc.setFontSize(size);
		doc.text(doc.splitTextToSize(clean(value), w - 3).slice(0, 2), x + 1.5, y);
	};
	const measure = (value, total, label, x, y, w, color, partial) => {
		const percent = scheduled
			? total > 0
				? 0
				: null
			: trainingTypeShare(value, total);
		text(
			number(value) +
				"/" +
				number(total) +
				label +
				(percent == null ? "" : " (" + percent + "%)") +
				(partial ? " *" : ""),
			x,
			y,
			w,
			6,
		);
		doc.setFillColor("#edf2f8");
		doc.rect(x + 1.5, y + 1, w - 3, 0.8, "F");
		if (percent > 0) {
			doc.setFillColor(color);
			doc.rect(
				x + 1.5,
				y + 1,
				((w - 3) * Math.min(percent, 100)) / 100,
				0.8,
				"F",
			);
		}
	};
	for (const block of blocks)
		for (let offset = 0; offset < Math.max(1, rows.length); offset += maxRows) {
			if (pages++) doc.addPage();
			text(
				data.rosterName + " | Allenamenti - tipologie per giocatore",
				margin,
				12,
				width,
				12,
				true,
			);
			text(
				"Stagione " +
					data.seasonName +
					" | " +
					moment(data.fromDate).format("DD/MM/YYYY") +
					" - " +
					moment(data.toDate).format("DD/MM/YYYY") +
					(scheduled
						? " | Sedute programmate | "
						: " | Solo sedute concluse | ") +
					(minutes ? "Minuti" : "Sedute") +
					(search ? " | Ricerca: " + search : ""),
				margin,
				19,
				width,
				8,
			);
			text(
				scheduled
					? "Dati individuali non ancora disponibili: presenze precompilate escluse, attivita non ancora svolta. Totali rosa riferiti alla programmazione."
					: "Valori giocatore/totale rosa, percentuale e barre sullo stesso tipo e periodo. Minuti attribuiti per presenza alla seduta intera.",
				margin,
				25,
				width,
				7,
			);
			let x = margin,
				y = 32;
			const typeWidth =
				(width - nameWidth - totalWidth - coverageWidth) /
				Math.max(1, block.length);
			rect(x, y, nameWidth, 12, "#f3f7fc");
			text("Giocatore", x, y + 5, nameWidth, 8, true);
			x += nameWidth;
			block.forEach((type) => {
				rect(x, y, typeWidth, 12, "#f3f7fc");
				text(type.type, x, y + 4, typeWidth, 7, true);
				text("Rosa: " + number(type[metric]) + unit, x, y + 9, typeWidth, 6);
				x += typeWidth;
			});
			if (!block.length) x = margin + nameWidth;
			const filler = block.length
				? 0
				: width - nameWidth - totalWidth - coverageWidth;
			if (filler) {
				rect(x, y, filler, 12, "#f3f7fc");
				text("Nessuna tipologia registrata", x, y + 5, filler);
				x += filler;
			}
			for (const [title, total] of [
				[minutes ? "Minuti" : "Sedute", summary?.[metric]],
			]) {
				rect(x, y, totalWidth, 12, "#f3f7fc");
				text(title, x, y + 4, totalWidth, 7, true);
				text("Rosa: " + number(total), x, y + 9, totalWidth, 6);
				x += totalWidth;
			}
			rect(x, y, coverageWidth, 12, "#f3f7fc");
			text(
				scheduled ? "Stato" : "Presenze da verificare",
				x,
				y + 4,
				coverageWidth,
				7,
				true,
			);
			y += 12;
			for (const player of rows.slice(offset, offset + maxRows)) {
				x = margin;
				const own = scheduled ? null : player.trainingTypes;
				rect(x, y, nameWidth, rowHeight, "#ffffff");
				text(
					player.name + (player.guest ? " *" : ""),
					x,
					y + 4,
					nameWidth,
					7,
					true,
				);
				x += nameWidth;
				for (const type of block) {
					rect(x, y, typeWidth, rowHeight, "#ffffff");
					measure(
						own ? trainingTypeValue(own, type, metric) : null,
						type[metric],
						unit,
						x,
						y + 4,
						typeWidth,
						typeColor(type, typeColors),
						trainingTypePartial(own, type, metric) ||
							own?.sessionsWithoutExercises > 0,
					);
					x += typeWidth;
				}
				if (filler) {
					rect(x, y, filler, rowHeight, "#ffffff");
					x += filler;
				}
				rect(x, y, totalWidth, rowHeight, "#f7faff");
				measure(
					own?.[metric],
					summary?.[metric],
					unit,
					x,
					y + 4,
					totalWidth,
					minutes ? "#739bc8" : "#64ad99",
					own?.unknownAttendance > 0 ||
						(minutes &&
							(own?.missingDuration > 0 || own?.sessionsWithoutExercises > 0)),
				);
				x += totalWidth;
				rect(x, y, coverageWidth, rowHeight, "#ffffff");
				text(
					scheduled ? "Non ancora svolto" : own ? own.unknownAttendance : "-",
					x,
					y + 4,
					coverageWidth,
				);
				y += rowHeight;
			}
			if (!rows.length)
				text(
					"Nessun giocatore corrisponde alla ricerca.",
					margin,
					y + 6,
					width,
				);
		}
	return pages;
}
